import { Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { TranslateService } from '@ngx-translate/core'
import type { Observable } from 'rxjs'
import { BehaviorSubject, catchError, map, of } from 'rxjs'

import { AuthorsService } from '../api/authors-service/authors.service'
import { FilterService } from '../api/filter-service/filter.service'
import { MagazineService } from '../api/magazine-service/magazine.service'
import type {
  FilterApiItem,
  FilterAuthor,
  FilterCategory,
  FilterItemType,
  FilterMagazine,
  FilterSelectionState,
  FilterType,
} from '../types/types'
import { FILTERABLE_ARTICLE_TYPES } from '../types/types'

type MapperFunction = (item: FilterApiItem, selected: boolean) => FilterItemType

@Injectable({
  providedIn: 'root',
})
export class FilterSelectionService {
  private readonly authorsService = inject(AuthorsService)
  private readonly magazineService = inject(MagazineService)
  private readonly filterService = inject(FilterService)
  private readonly translate = inject(TranslateService)

  private static readonly PAGE_SIZE = 10

  private readonly state = new BehaviorSubject<FilterSelectionState>({
    magazines: [],
    authors: [],
    types: this.initializeArticleTypes(),
  })

  private magazinePage = 1
  private authorPage = 1
  private typePage = 1

  // Track whether magazines have been initially loaded
  private magazinesInitialized = false

  private readonly categoryMappers: Record<FilterCategory, MapperFunction> = {
    magazines: (item, selected) => ({
      name: item.title ?? '',
      selected,
      id: Number(item.id),
    }),
    authors: (item, selected) => ({
      id: Number(item.id),
      name: `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim(),
      selected,
      additionalProps: {
        first_name: item.first_name,
        last_name: item.last_name,
      },
    }),
    types: (item, selected) => ({
      id: item.id as string,
      name: item.name ?? '',
      selected,
    }),
  }

  private pages: Record<FilterCategory, number> = {
    magazines: 1,
    authors: 1,
    types: 1,
  }

  readonly magazines$ = this.state.pipe(map(state => state.magazines))
  readonly authors$ = this.state.pipe(map(state => state.authors))
  readonly types$ = this.state.pipe(map(state => state.types))

  constructor() {
    // Initialize the article types after constructor is fully initialized
    setTimeout(() => {
      const types = this.initializeArticleTypes()
      if (types && types.length > 0) {
        this.updateTypes(types)
      }
    }, 0)

    // Retranslate the article type names when the active language changes, keeping selections intact
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.retranslateTypeNames()
    })
  }

  private retranslateTypeNames(): void {
    const currentTypes = this.state.value.types
    if (currentTypes.length === 0) return

    const retranslated = currentTypes.map(type => ({
      ...type,
      name: this.formatArticleTypeName(type.id),
    }))

    this.state.next({ ...this.state.value, types: retranslated })
  }

  private initializeArticleTypes(): FilterType[] {
    // Only the types in FILTERABLE_ARTICLE_TYPES may appear in the filter
    const types = FILTERABLE_ARTICLE_TYPES.map(type => ({
      id: type,
      name: this.formatArticleTypeName(type),
      selected: false,
    }))

    // Return empty array if something went wrong
    if (!types || types.length === 0) {
      console.warn('Failed to initialize article types from enum')
      return []
    }

    return types
  }

  private formatArticleTypeName(type: string): string {
    return String(this.translate.instant('articleTypes.' + type))
  }

  updateMagazineSelection(ids: number[]): void {
    const currentState = { ...this.state.value }
    const magazines = currentState.magazines.map(magazine => ({
      ...magazine,
      selected: ids.includes(magazine.id),
    }))
    this.state.next({ ...currentState, magazines })
  }

  updateAuthorSelection(ids: number[]): void {
    const currentState = { ...this.state.value }
    const authors = currentState.authors.map(author => ({
      ...author,
      selected: ids.includes(author.id),
    }))
    this.state.next({ ...currentState, authors })
  }

  updateMagazines(magazines: FilterMagazine[]): void {
    // If we're trying to set empty magazines but we already have initialized the list, abort
    if (
      (magazines?.length ?? 0) === 0 &&
      this.magazinesInitialized &&
      this.state.value.magazines.length > 0
    ) {
      console.warn(
        'Attempted to update magazines with empty array after initialization, keeping existing magazines'
      )
      return
    }

    // Create a safe array, either from input or empty if null/undefined
    const safeArray = magazines ?? []

    // If we have existing magazines with selections, preserve those selections
    if (this.state.value.magazines.length > 0 && safeArray.length > 0) {
      const selectedIds = this.state.value.magazines
        .filter(m => m.selected)
        .map(m => m.id)

      // Preserve selections in the new array
      if (selectedIds.length > 0) {
        safeArray.forEach(mag => {
          mag.selected = selectedIds.includes(mag.id)
        })
      }
    }

    // Update state with the new magazine array
    this.state.next({
      ...this.state.value,
      magazines: safeArray,
    })

    // Mark as initialized if we have magazines
    if (safeArray.length > 0) {
      this.magazinesInitialized = true
    }
  }

  updateAuthors(authors: FilterAuthor[]): void {
    // If authors is empty, preserve existing state
    if (!authors || authors.length === 0) {
      return
    }

    // Get current state to preserve selections
    const currentAuthors = this.state.value.authors
    const selectedIds = currentAuthors.filter(a => a.selected).map(a => a.id)

    // Map new authors array while preserving selected state
    const updatedAuthors = authors.map(author => ({
      ...author,
      selected: selectedIds.includes(author.id)
        ? true
        : (author.selected ?? false),
    }))

    // Sort authors - selected ones go to the top
    const selectedAuthors = updatedAuthors.filter(author => author.selected)
    const nonSelectedAuthors = updatedAuthors.filter(author => !author.selected)
    const sortedAuthors = [...selectedAuthors, ...nonSelectedAuthors]

    // Update state with new array
    this.state.next({
      ...this.state.value,
      authors: sortedAuthors,
    })
  }

  updateTypes(types: FilterType[]): void {
    // Skip update if the new array is empty but we already have types
    if (types.length === 0 && this.state.value.types.length > 0) {
      console.warn(
        'Attempted to update types with empty array, keeping existing types'
      )
      return
    }

    // Update the state
    this.state.next({ ...this.state.value, types })
  }

  toggleSelection(
    category: FilterCategory,
    item: FilterMagazine | FilterAuthor | FilterType
  ): number[] {
    try {
      const currentState = { ...this.state.value }
      const items = [...currentState[category]]
      const index = items.findIndex(i => i.id === item.id)

      if (index !== -1) {
        // Toggle the selected state
        const newSelected = !items[index].selected

        // Create a new copy of the item with the toggled selected state
        items[index] = {
          ...items[index],
          selected: newSelected,
        }

        // Create a new state object and emit it
        this.state.next({
          ...currentState,
          [category]: items,
        })

        // Return the IDs of all selected items
        return items.filter(i => i.selected).map(i => Number(i.id))
      }

      return items.filter(i => i.selected).map(i => Number(i.id))
    } catch {
      return []
    }
  }

  clearSelection(category: FilterCategory): void {
    const items = this.state.value[category].map(item => ({
      ...item,
      selected: false,
    }))
    this.state.next({ ...this.state.value, [category]: items })
  }

  getSelectedCount(category: FilterCategory): Observable<number> {
    return this.state.pipe(
      map(state => state[category].filter(item => item.selected).length)
    )
  }

  getCurrentAuthors(): FilterAuthor[] {
    return this.state.value.authors
  }

  getCurrentMagazines(): FilterMagazine[] {
    return this.state.value.magazines
  }

  getCurrentTypes(): FilterType[] {
    return this.state.value.types
  }

  getCurrentMagazinePage(): number {
    return this.magazinePage
  }

  getCurrentTypePage(): number {
    return this.typePage
  }

  setCurrentAuthorPage(page: number): void {
    this.authorPage = page
  }

  getCurrentAuthorPage(): number {
    return this.authorPage
  }

  loadMoreMagazines(page: number): Observable<FilterMagazine[]> {
    // Don't load if we're already at this page
    if (page <= this.magazinePage) {
      return of([])
    }

    this.magazinePage = page
    const currentMagazines = this.getCurrentMagazines()
    const selectedIds = currentMagazines.filter(m => m.selected).map(m => m.id)

    return this.magazineService.getMagazines(true, true).pipe(
      map(response => {
        // Filter out any magazines we already have
        const existingIds = new Set(currentMagazines.map(m => m.id))
        return response.items
          .filter(magazine => !existingIds.has(magazine.id))
          .map(magazine => ({
            name: magazine.title ?? '',
            selected: selectedIds.includes(magazine.id),
            id: magazine.id,
          }))
      }),
      catchError(() => {
        return of([])
      })
    )
  }

  loadMoreTypes(page: number): Observable<FilterType[]> {
    this.typePage = page

    // Article types are already loaded from the enum
    // No need to call the API - just return empty array as there are no more to load
    return of([])
  }

  loadMoreAuthors(page: number): Observable<FilterAuthor[]> {
    this.authorPage = page
    const currentAuthors = this.getCurrentAuthors()
    const selectedIds = currentAuthors.filter(a => a.selected).map(a => a.id)

    return this.authorsService
      .getAuthors(page, FilterSelectionService.PAGE_SIZE)
      .pipe(
        map(response => {
          if (!response?.Authors?.docs) {
            return []
          }

          // Map the new authors from the response
          const newAuthors = response.Authors.docs.map(author => ({
            id: author.id,
            name: `${author.first_name ?? ''} ${author.last_name ?? ''}`.trim(),
            selected: selectedIds.includes(author.id),
            additionalProps: {
              first_name: author.first_name,
              last_name: author.last_name,
              pre_title: author.pre_title,
              middle_name: author.middle_name,
              initials: author.initials,
              post_title: author.post_title,
            },
          }))

          // If it's the first page, get all selected authors and put them at the top
          if (page === 1) {
            const selectedAuthors = currentAuthors.filter(
              author => author.selected
            )
            const nonSelectedNewAuthors = newAuthors.filter(
              author => !selectedIds.includes(author.id)
            )
            return [...selectedAuthors, ...nonSelectedNewAuthors]
          }

          // For subsequent pages, just return the new authors
          return newAuthors
        }),
        catchError(() => {
          return of([])
        })
      )
  }

  getState(): Observable<FilterSelectionState> {
    return this.state.asObservable()
  }

  getCurrentState(): FilterSelectionState {
    return this.state.value
  }
}
